import callApi, { CallApiResult, getCustomerId } from 'src/api';
import { RegionKeys } from 'src/core/provider/AppMeta';
import { AdditionalInvoiceData, AddressCategory } from 'src/entities/basic-types/Address';
import { SCHEMA_ADDRESS_SHAPE } from 'src/schemas/MyAddressBook';
import { SCHEMA_ORDER_ADDRESS_SHAPE } from 'src/schemas/OrderingAddress';
import { AccountID } from '../organization';

/**
 * Wire shape of the my-data address form (AddressRequestDto/AddressDto): `type` carries the
 * backend-pinned classification values, unlike the legacy form schema's FE-only private/business.
 */
export interface AddressSavePayload {
    type: AddressCategory;
    vatNumber: string;
    /** Round-tripped, never edited in the form — the update PUT is a full-entity replace. */
    addressChangeReason: string;
    addresseeName: string;
    addresseeName2: string;
    street: string;
    streetNumber: string;
    postalCode: string;
    city: string;
    countryIso2: RegionKeys;
    contactFirstName: string;
    contactLastName: string;
    /** Omitted (not '') when no phone is selected — the DTO field is a UUID. */
    userPhoneNumberId?: string;
    invoiceMailAddressIds: string[];
    deliveryMailAddressIds: string[];
    additionalInvoiceData: AdditionalInvoiceData[];
    isDefaultBillingAddress: boolean;
    isDefaultDeliveryAddress: boolean;
}

/** userservice AddressDefaultOptionsDto: what the customer may pick as the address defaults. */
export interface AddressDefaultOptions {
    /** normalized_name tokens, e.g. 'PREPAYMENT'. */
    paymentMethods: string[];
    carriers: { id: number; name: string }[];
}

/** userservice AddressDefaultsDto; null = none. */
export interface AddressDefaultsPayload {
    defaultPaymentMethod: string | null;
    standardCarrierId: string | null;
}

/* Customer Addresses */

export function fetchCustomerAddresses(accountId: AccountID = getCustomerId() as AccountID): Promise<CallApiResult> {
    const endpoint = `/api/customer/${accountId}/addresses`;
    return callApi(endpoint);
}

// TODO: remove legacy name after all imports migrate to fetchCustomerAddresses
export const getCustomerAddresses = fetchCustomerAddresses;

export function fetchCustomerAddressUsages(
    accountId: AccountID = getCustomerId() as AccountID
): Promise<CallApiResult> {
    const endpoint = `/api/customer/${accountId}/addresses/usages`;
    return callApi(endpoint);
}

/**
 * The legacy form schema's `type` is FE-only ('private'|'business') and must never reach the wire —
 * the DTO field carries the AddressCategory classification. The new payload's 'Privat'/'Firma'
 * pass through untouched.
 */
export function prepareAddressSubmitData(
    data: SCHEMA_ADDRESS_SHAPE | AddressSavePayload
): Omit<SCHEMA_ADDRESS_SHAPE, 'type'> | AddressSavePayload {
    const additionalInvoiceData = data.additionalInvoiceData.filter(d => d.value != null && d.value.length > 0);
    if (data.type === 'private' || data.type === 'business') {
        const { type, ...legacyData } = data;
        return { ...legacyData, additionalInvoiceData };
    }
    return { ...data, additionalInvoiceData };
}

export function submitAddressForm(
    accountId: AccountID = 'current',
    data: SCHEMA_ADDRESS_SHAPE | AddressSavePayload,
    id?: string | number
): Promise<CallApiResult> {
    const preparedData = prepareAddressSubmitData(data);

    if (id === undefined)
        return callApi(`/api/customer/${accountId}/addresses`, { method: 'POST', data: preparedData });

    return callApi(`/api/customer/${accountId}/addresses/${id}`, { method: 'PUT', data: preparedData });
}

export function deleteCustomerAddress(
    addressId: string | number,
    accountId: AccountID = 'current'
): Promise<CallApiResult> {
    return callApi(`/api/customer/${accountId}/addresses/${addressId}`, { method: 'DELETE' });
}

/**
 * Transfers every transferable usage (default roles, Abo references) from the old to the new
 * address server-side; the old address itself is NOT deleted by this call.
 */
export function replaceCustomerAddress(
    oldAddressId: string | number,
    newAddressId: string | number,
    accountId: AccountID = 'current'
): Promise<CallApiResult> {
    return callApi(`/api/customer/${accountId}/addresses/${oldAddressId}/replaceWith/${newAddressId}`, {
        method: 'PUT',
    });
}

export function fetchAddressDefaultOptions(
    countryIso2: string,
    accountId: AccountID = 'current'
): Promise<CallApiResult<AddressDefaultOptions>> {
    return callApi(`/api/customer/${accountId}/addresses/defaultOptions`, { params: { countryIso2 } });
}

export function putAddressDefaults(
    addressId: string | number,
    payload: AddressDefaultsPayload,
    accountId: AccountID = 'current'
): Promise<CallApiResult<AddressDefaultsPayload>> {
    return callApi(`/api/customer/${accountId}/addresses/${addressId}/defaults`, { method: 'PUT', data: payload });
}

/* Standard-flag unset — the address PUT can only set the flags; the DELETEs are idempotent. */

export function deleteDefaultDeliveryAddressFlag(
    addressId: string | number,
    accountId: AccountID = 'current'
): Promise<CallApiResult> {
    return callApi(`/api/customer/${accountId}/addresses/${addressId}/defaultDeliveryAddress`, { method: 'DELETE' });
}

export function deleteDefaultBillingAddressFlag(
    addressId: string | number,
    accountId: AccountID = 'current'
): Promise<CallApiResult> {
    return callApi(`/api/customer/${accountId}/addresses/${addressId}/defaultBillingAddress`, { method: 'DELETE' });
}

export function submitOrderingAddressForm(
    data: SCHEMA_ORDER_ADDRESS_SHAPE,
    id?: string | number
): Promise<CallApiResult> {
    const preparedData = {
        ...data,
        id,
        additionalInvoiceData: data.additionalInvoiceData.filter(d => d.value != null && d.value.length > 0),
    };
    return callApi(`/api/ordering/address/${'current'}`, { method: 'PUT', data: preparedData });
}
